/**
 * The AI Team page (ADR 0259, issue #682): everything the app's AI found on
 * its own, as three named employees, for everyone (Jonathon, 2026-10-03:
 * "everyone can see it").
 *
 * One page, flipped at the top between Iris's inbox and the two ways the
 * mockup Josh chose on 2026-10-03 drew the findings (layouts B and C):
 *
 *   Inbox   the customers still waiting on a reply, which was the Messages
 *           tab until Josh moved every AI onto this page on 2026-10-04
 *   Feed    a morning standup: each finding written as a message from the
 *           employee who found it, newest first, filtered by employee, with
 *           the dismissed ones a tab away
 *   By job  the jobs the AI flagged, the one waiting longest first, and
 *           everything every employee says about the chosen one
 *
 * Anyone may dismiss a finding as wrong, or as right but fine, and reopen it
 * (Jonathon, 2026-10-04: "everyone can"). That is the same
 * dismissal the job page, My list and Messages honour, so it goes from there
 * too, and the reason is what each employee's count of "wrong" is made of.
 */

import { useMemo, useState } from "react";
import type { ReactElement } from "react";

import {
  AI_DISMISSED_DAYS,
  AI_EMPLOYEE_ORDER,
  AI_EMPLOYEES,
  aiDismissedJobs,
  aiFindings,
  aiFindingsByJob,
  aiTeamCounts,
  aiWaitingDays,
  formatMoney,
  hasValue,
  isSold,
  REPLY_READ_DAYS,
  STAGE_LABEL,
} from "@roof-hawk/core";
import type { AiEmployee, AiFinding, AiReason, Cents } from "@roof-hawk/core";

import { ErrorNote } from "../../components/ui";
import { MessagesScreen } from "../messages/MessagesScreen";
import { useAsync } from "../../lib/hooks";
import {
  dismissAiFinding,
  fetchAiTeam,
  reopenAiFinding,
} from "../../lib/queries";
import { useSession } from "../../session";
import type { AppData } from "../shell/Shell";
import {
  Avatar,
  BID_LABEL,
  DismissedRow,
  FindingCard,
  JobPanel,
  JobRow,
  nameOfJob,
  partOfDay,
  sum,
  waited,
  type JobFacts,
} from "./AiTeamParts";

type Mode = "inbox" | "feed" | "jobs";
type Who = AiEmployee | "all";

/** How a finding is drawn: the feed's voice, or the by-job view's title. */
type Draw = {
  card: (finding: AiFinding, voice: boolean) => ReactElement;
  history: (finding: AiFinding) => ReactElement;
};

export function AiTeamScreen({ data }: { data: AppData }) {
  const { people } = useSession();
  const team = useAsync(() => fetchAiTeam(), []);
  const [mode, setMode] = useState<Mode>("feed");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const contactName = useMemo(
    () => new Map(data.contacts.map((c) => [c.id, c.full_name])),
    [data.contacts],
  );
  const jobById = useMemo(
    () => new Map(data.jobs.map((job) => [job.id, job])),
    [data.jobs],
  );
  const personName = (id: string | null) =>
    (id && people.find((p) => p.id === id)?.full_name) || "Someone";
  const facts = (jobId: string | null): JobFacts | null => {
    const job = jobId ? jobById.get(jobId) : undefined;
    if (!job) return null;
    const value = data.values?.get(job.id);
    return {
      number: `#${job.job_number}`,
      name: nameOfJob(job, contactName),
      stage: STAGE_LABEL[job.stage],
      rep: personName(job.rep_id),
      bid: (job.bid_type && BID_LABEL[job.bid_type]) || "",
      value: hasValue(value) ? formatMoney(value as Cents) : "No price yet",
    };
  };

  const findings = useMemo(
    () =>
      team.data
        ? aiFindings(
            {
              ...team.data,
              jobs: data.jobs.map((job) => ({
                id: job.id,
                name: nameOfJob(job, contactName),
                sold: isSold(job),
              })),
            },
            new Date(),
          )
        : [],
    [team.data, data.jobs, contactName],
  );

  async function act(finding: AiFinding, reason: AiReason | null) {
    setBusy(finding.key);
    setError(null);
    try {
      if (reason) {
        await dismissAiFinding(finding.source, finding.sourceId, reason);
      } else {
        await reopenAiFinding(finding.source, finding.sourceId);
      }
      await team.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const now = new Date();
  const draw: Draw = {
    card: (finding, voice) => (
      <FindingCard
        key={finding.key}
        finding={finding}
        job={facts(finding.jobId)}
        days={aiWaitingDays(finding, now)}
        voice={voice}
        busy={busy === finding.key}
        onDismiss={(reason) => void act(finding, reason)}
      />
    ),
    history: (finding) => (
      <DismissedRow
        key={finding.key}
        finding={finding}
        job={facts(finding.jobId)}
        by={personName(finding.dismissedBy)}
        busy={busy === finding.key}
        onReopen={() => void act(finding, null)}
      />
    ),
  };

  return (
    <div className="ait">
      <div className="listhead">
        <div>
          <h2>AI Team</h2>
          <div className="ait-sub">{SUBTITLE[mode]}</div>
        </div>
        <div className="ait-flip" role="group" aria-label="Show">
          <button
            type="button"
            aria-pressed={mode === "inbox"}
            onClick={() => setMode("inbox")}
          >
            Inbox
          </button>
          <button
            type="button"
            aria-pressed={mode === "feed"}
            onClick={() => setMode("feed")}
          >
            Feed
          </button>
          <button
            type="button"
            aria-pressed={mode === "jobs"}
            onClick={() => setMode("jobs")}
          >
            By job
          </button>
        </div>
      </div>
      <ErrorNote error={team.error ?? error} />

      {mode === "inbox" ? (
        <InboxView data={data} />
      ) : team.loading && !team.data ? (
        <div className="ait-empty">Loading what the team found…</div>
      ) : mode === "feed" ? (
        <FeedView findings={findings} now={now} draw={draw} />
      ) : (
        <JobsView findings={findings} now={now} draw={draw} facts={facts} />
      )}
    </div>
  );
}

const SUBTITLE: Record<Mode, string> = {
  inbox: "Customers still waiting on a reply, sorted by what they need.",
  feed: "What each AI employee found, written as a message from them.",
  jobs: "Everything every AI employee says about a job, in one place.",
};

/** Iris's inbox: the customers waiting on a reply (ADR 0227, ADR 0259). */
function InboxView({ data }: { data: AppData }) {
  return (
    <>
      <div className="ait-by ait-inbox-by">
        <Avatar employee="iris" size="md" /> <b>{AI_EMPLOYEES.iris.name}</b>{" "}
        <span className="ait-muted">
          {AI_EMPLOYEES.iris.role} · read every customer text and email from the
          last {REPLY_READ_DAYS} days
        </span>
      </div>
      <MessagesScreen data={data} embedded />
    </>
  );
}

/** Layout B: the morning standup. */
function FeedView({
  findings,
  now,
  draw,
}: {
  findings: AiFinding[];
  now: Date;
  draw: Draw;
}) {
  const [who, setWho] = useState<Who>("all");
  const [tab, setTab] = useState<"open" | "dismissed">("open");
  const counts = aiTeamCounts(findings, now);
  const allOpen = findings.filter((f) => f.status === "open");
  const mine = findings.filter((f) => who === "all" || f.employee === who);
  const open = mine.filter((f) => f.status === "open");
  const dismissed = mine
    .filter((f) => f.status === "dismissed")
    .sort((a, b) => (b.dismissedAt ?? "").localeCompare(a.dismissedAt ?? ""));

  return (
    <>
      <div className="ait-chips">
        {(["all", ...AI_EMPLOYEE_ORDER] as Who[]).map((w) => (
          <button
            type="button"
            key={w}
            className="ait-chip"
            aria-pressed={who === w}
            onClick={() => setWho(w)}
          >
            {w === "all" ? (
              "Everyone"
            ) : (
              <>
                <Avatar employee={w} size="xs" /> {AI_EMPLOYEES[w].name}
              </>
            )}
            <span className="ait-n">
              {w === "all" ? allOpen.length : counts[w].open}
            </span>
          </button>
        ))}
      </div>

      <div className="ait-tabs" role="group" aria-label="Which findings">
        <button
          type="button"
          aria-pressed={tab === "open"}
          onClick={() => setTab("open")}
        >
          Open ({open.length})
        </button>
        <button
          type="button"
          aria-pressed={tab === "dismissed"}
          onClick={() => setTab("dismissed")}
        >
          Dismissed ({dismissed.length})
        </button>
      </div>

      {tab === "dismissed" ? (
        <div className="ait-card">
          {dismissed.length ? (
            dismissed.map(draw.history)
          ) : (
            <div className="ait-muted">
              Nothing dismissed in the last {AI_DISMISSED_DAYS} days.
            </div>
          )}
          <div className="ait-muted ait-foot">
            Last {AI_DISMISSED_DAYS} days. Reopen puts it back on the job page
            and My list.
          </div>
        </div>
      ) : (
        <>
          <section className="ait-card ait-standup" aria-label="Standup">
            <h3>Good {partOfDay(now)}. Here’s what the team found.</h3>
            <div className="ait-standup-rows">
              {AI_EMPLOYEE_ORDER.map((e) => (
                <div key={e}>
                  <Avatar employee={e} size="sm" />{" "}
                  <b>{AI_EMPLOYEES[e].name}</b>{" "}
                  <span className="ait-muted">
                    {counts[e].open} open · {counts[e].wrong} marked wrong this
                    month
                  </span>
                </div>
              ))}
            </div>
            <div className="ait-muted">
              Newest first.{" "}
              {allOpen.filter((f) => aiWaitingDays(f, now) < 1).length} new
              since yesterday;{" "}
              {allOpen.filter((f) => aiWaitingDays(f, now) > 3).length} have
              waited more than 3 days.
            </div>
          </section>
          {open.length ? (
            open.map((f) => draw.card(f, true))
          ) : (
            <div className="ait-empty">Nothing open. The team is quiet.</div>
          )}
        </>
      )}
    </>
  );
}

/** Layout C: everything about one job at a time. */
function JobsView({
  findings,
  now,
  draw,
  facts,
}: {
  findings: AiFinding[];
  now: Date;
  draw: Draw;
  facts: (jobId: string | null) => JobFacts | null;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const counts = aiTeamCounts(findings, now);
  const allOpen = findings.filter((f) => f.status === "open");
  const groups = aiFindingsByJob(findings);
  const gone = aiDismissedJobs(findings);
  const group =
    [...groups, ...gone].find((g) => g.jobId === picked) ??
    groups[0] ??
    gone[0] ??
    null;
  const flagged = sum(counts, "flagged");
  const wrong = sum(counts, "wrong");

  return (
    <>
      <div className="scoreboard">
        <div className="sb-fig">
          <span className="k">Open</span>
          <b>{allOpen.length}</b>
          <span className="ait-muted">
            Across {groups.length} {groups.length === 1 ? "job" : "jobs"}
          </span>
        </div>
        <div className="sb-fig">
          <span className="k">Flagged this month</span>
          <b>{flagged}</b>
          <span className="ait-muted">
            By {AI_EMPLOYEE_ORDER.length} AI employees
          </span>
        </div>
        <div className="sb-fig">
          <span className="k">Marked wrong</span>
          <b>{wrong}</b>
          <span className="ait-muted">
            {flagged
              ? `${Math.round((wrong / flagged) * 100)}% of this month’s`
              : "Nothing flagged this month"}
          </span>
        </div>
        <div className="sb-fig ait-late">
          <span className="k">Waiting over 3 days</span>
          <b>{allOpen.filter((f) => aiWaitingDays(f, now) > 3).length}</b>
          <span className="ait-muted">Nobody has looked</span>
        </div>
      </div>

      {groups.length + gone.length === 0 ? (
        <div className="ait-empty">The AI hasn’t flagged any job.</div>
      ) : (
        <div className="ait-jobs">
          <div className="ait-card ait-joblist">
            <div className="ait-joblist-h">
              <h3>Jobs the AI flagged</h3>
              <div className="ait-muted">Oldest waiting first</div>
            </div>
            <div aria-label="Jobs the AI flagged">
              {groups.map((g) => (
                <JobRow
                  key={g.jobId}
                  group={g}
                  job={facts(g.jobId)}
                  on={g.jobId === group?.jobId}
                  days={waited(
                    Math.max(...g.open.map((f) => aiWaitingDays(f, now))),
                  )}
                  onPick={() => setPicked(g.jobId)}
                />
              ))}
            </div>
            {gone.length ? (
              <>
                <div className="ait-sec">
                  Dismissed in the last {AI_DISMISSED_DAYS} days
                </div>
                <div aria-label="Jobs with dismissed findings">
                  {gone.map((g) => (
                    <JobRow
                      key={g.jobId}
                      group={g}
                      job={facts(g.jobId)}
                      on={g.jobId === group?.jobId}
                      days={null}
                      onPick={() => setPicked(g.jobId)}
                    />
                  ))}
                </div>
              </>
            ) : null}
          </div>
          {group ? (
            <JobPanel
              group={group}
              job={facts(group.jobId)}
              card={(f) => draw.card(f, false)}
              history={draw.history}
            />
          ) : null}
        </div>
      )}
    </>
  );
}
